import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { CSSTransition } from 'react-transition-group';
import Scroll from 'scroll-utility';
import styled from 'styled-components';

import { preferenceActions } from '../../actions';
import fetchStockExpertTips from '../../actions/tips/fetchStockExpertTips';
import { generalErrorHandler } from '../../actions/utils';
import { RetailInstitutionalTradesEnum } from '../../enums';
import { uiHelpers } from '../../helpers';
import { useAnalytics } from '../../helpers/analytics.helper';
import {
  eligibleForHKEXProfile,
  stockHasMarketSMA,
  stockIsHKIndex,
  stockIsIndex,
  stockIsUSIndex,
} from '../../helpers/stockCode.helper';
import { useBidAsk } from '../../hooks/useBidAsk';
import useDispatch from '../../hooks/useDispatch';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { usePreferences } from '../../hooks/usePreferences';
import { useMembership, useUser } from '../../hooks/users';
import useSelector from '../../hooks/useSelector';
import BlurPlaceholder from '../../modules/FreeUser/BlurPlaceholder';
import { TIPS_TAB_KEY } from '../../modules/Insight/StockPageStatisticsTable';
import {
  Category,
  StockLookupEntity,
} from '../../py-generated/datalouder/stock/types';
import { dailypriceService, miscService, stockService } from '../../services';
import { isStockLookupRedirectResponse } from '../../services/stock.service';
import { Analytics, Region, TimeSeries } from '../../shared/types';
import { PriceDataPoint, ProbabilityData, SignalOccurrence } from '../../types';
import { categoryToRegin } from '../stock/stockCategory';
import CCASSDescription from './CCASSDescription';
import AAII from './generators/AAII';
import ATR from './generators/ATR';
import { ChartGenerator } from './generators/base';
import BB from './generators/BB';
import Comparison from './generators/Comparison';
import Concentration from './generators/Concentration';
import ConstituentSMA from './generators/ConsituentSMA';
import Favorites from './generators/Favorites';
import FearGreedIndex from './generators/FearGreedIndex';
import HSIDividend from './generators/HSIDividend';
import HSIPB from './generators/HSIPB';
import HSIPE from './generators/HSIPE';
import HSIShortRatio from './generators/HSIShortRatio';
import MA from './generators/MA';
import MACD from './generators/MACD';
import NewVolume from './generators/NewVolume';
import NOP from './generators/NOP';
import NOPStrategy from './generators/NOPStrategy';
import NOPStrategySignal from './generators/NOPStrategySignal';
import NOPStrategySignalArea from './generators/NOPStrategySignalArea';
import Probability from './generators/Probability';
import RetailInstitutionalTrades from './generators/RetailInstitutionalTrades';
import RSI from './generators/RSI';
import SignalOccurence from './generators/SignalOccurence';
import Southbound from './generators/Southbound';
import Tips from './generators/Tips';
import Turnover from './generators/Turnover';
import Volume from './generators/Volume';
import ComparisonSearch from './modules/comparisonSearch';
import PriceViewerChart from './PriceViewerChart';
import PriceViewerPreferences from './PriceViewerPreferences';
export const DL_SWITCH_CLASS_NAME = 'dl-switch';
export const DL_SWITCH_LEAD_CLASS_NAME = 'dl-switch-lead';
export const GENERAL_DL_SWITCH_CLASS = `${DL_SWITCH_CLASS_NAME} ${DL_SWITCH_LEAD_CLASS_NAME}`;
export const DL_SWITCH_LINE_WRAPPER_CLASS_NAME = 'dl-switch-line-wrapper';

interface PriceViewerProps {
  stockCode: string;
  category?: Category;
  probabilityData?: ProbabilityData;
  regionOverride?: Region;
  scrollToChartWhenComplete?: boolean;
  signalOccurrences?: SignalOccurrence[];
  influencerId?: number;
  noLittleSearchField?: boolean;
  stockCodeList?: string;
  isCryptoStock?: boolean;
  handleComparisonModal?: (show: boolean) => void;
  showComparisonModal?: boolean;
  showPriceViewerPreferences?: boolean;
  minuteDataState?: string;
  consituentSMAState?: string;
}

const awsDTToEChartDT = (dt: string): string => {
  const dateStr = `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}`;
  if (dt.length === 12) {
    return dateStr + ' ' + dt.slice(8, 10) + ':' + dt.slice(10, 12);
  }
  return dateStr;
};

interface MiniStockInfo {
  category: Category;
  ticker: string;
  name: string;
}

export interface ComparisonPrices {
  name: string;
  // It is an  array to store data of ComparisonPrices the format will be [date:string,price:number]
  data: (string | number)[][];
}

const getOLHCVQueryString = (stockInfo: MiniStockInfo): string =>
  `olhcv:${stockInfo.category}#${stockInfo.ticker}`;

const getCCASSQueryString = (stockInfo: MiniStockInfo): string =>
  `ccass:${stockInfo.category}#${stockInfo.ticker}`;

const getMarketBreadthQueryString = (stockInfo: MiniStockInfo): string =>
  `marketbreadth:${stockInfo.category}#${stockInfo.ticker}`;

const getNOPStrategyString = (stockInfo: MiniStockInfo): string =>
  `NOPSTRATEGY:${stockInfo.category}#${stockInfo.ticker}`;

const getDisplayNOPStrategy = (
  rawTimeSeries: TimeSeries.Item[],
  stockInfo: MiniStockInfo
): boolean => {
  for (const item of rawTimeSeries) {
    if (item[getNOPStrategyString(stockInfo)]) {
      return true;
    }
  }
  return false;
};

export const getActiveStrategyByName = (name: string): string => {
  return name === 'alpha' ? 'stategy_0' : 'stategy_1';
};

const Description = styled.div`
  font-size: 12px;
  font-weight: 500;
  padding: 0 5%;
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
const PriceViewer: React.FC<PriceViewerProps> = React.memo(
  function PriceViewer({
    stockCode,
    category,
    scrollToChartWhenComplete,
    regionOverride = null,
    probabilityData = null,
    signalOccurrences = null,
    influencerId,
    noLittleSearchField = false,
    isCryptoStock = false,
    handleComparisonModal,
    showComparisonModal,
    showPriceViewerPreferences = true,
  }) {
    const dispatch = useDispatch();
    const user = useUser();
    const isAdmin = user && user.isAdmin;
    const enablePd = isAdmin || user?.id === 2795;
    const {
      region: defaultRegion,
      priceViewerVolumeOverlap,
      priceViewerTurnoverOverlap,
      period,
      unit,
      minuteUnit,
      showMA,
      MAType,
      MAIntervals,
      showRSI,
      RSIInterval,
      showATR,
      ATRInterval,
      showTopN,
      priceViewerTopN,
      showMCAD,
      showBollingerBand,
      showParticipantsNumber,
      showShiftedParticipantsNumber,
      showIndexSMA,
      indexSMAIntervals,
      showMarketSMA,
      marketSMAIntervals,
      showRetailTrades,
      showInstitutionalTrades,
      priceViewerLabelType,
      graphColorStyle,
      retailTradesVersion,
      institutionalTradesVersion,
      nopColorStyleV2,
      concentrationColorStyleV2,
      comparisonColorStyle,
      showFearGreedIndex,
      showAAII,
      showHSIPE,
      showHSIPB,
      showHSIFundamental,
      showHSIShortRatio,
      showHSIDividend,
      showSouthbound,
      showNOPStrategy,
      NOPStrategyVersion,
      strategyMode,
    } = usePreferences();

    const availableFeatures = useFeatureFlags();
    const isUnitMinute = unit === 'minute';
    const { t, i18n } = useTranslation();

    const [region, setRegion] = useState(regionOverride || defaultRegion);
    const [firstLoad, setFirstLoad] = useState(true);
    const [loadingStockInfo, setLoadingStockInfo] = useState(false);
    const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
    const [stock, setStock] = useState<StockLookupEntity>();
    const [rawTimeSeries, setRawTimeSeries] = useState<TimeSeries.Item[]>([]);
    const [error, setError] = useState<Error>();

    const [currentStockCode, setCurrentStockCode] = useState<string>(stockCode);
    const [editingStockCode, setEditingStockCode] = useState<string>(stockCode);
    const [currentCategory, setCurrentCategory] = useState<string>(category);
    const [currentStock, setCurrentStock] = useState<MiniStockInfo>();
    const [comparisonStockList, setComparisonStockList] = useState<
      MiniStockInfo[]
    >([]);
    const isSouthboundStock = useMemo(() => {
      return rawTimeSeries.some(item => {
        const ccass = item[getCCASSQueryString(currentStock)] || {};
        return ccass['southbound_a00003'] || ccass['southbound_a00004'];
      });
    }, [rawTimeSeries, currentStock]);
    useEffect(() => {
      // reset prices when switching between minute and daily or change stockcode
      setRawTimeSeries([]);
    }, [isUnitMinute, currentStockCode, stockCode]);

    const handleEditingStockCode = useCallback(
      (value: string) => setEditingStockCode(value),
      []
    );

    useEffect(() => {
      // When outside changes the stockCode, the currentStockCode should be reset.
      setCurrentStockCode(stockCode);
    }, [stockCode]);

    useEffect(() => {
      setCurrentCategory(category);
    }, [category]);

    const handleSearchClick = useCallback(
      () => setCurrentStockCode(editingStockCode),
      [editingStockCode]
    );

    const handleSearchInputKeyPressed: React.KeyboardEventHandler<HTMLInputElement> =
      useCallback(
        e => {
          currentStockCode !== '' &&
            (e.keyCode || e.which) === 13 &&
            handleSearchClick();
        },
        [currentStockCode, handleSearchClick]
      );

    const [queue] = useAnalytics();

    const wrapAnalytics = (dataKey: string, callback: () => void) => () => {
      queue(
        Analytics.ActionEnum.ChangePriceChartPreference,
        currentStockCode,
        dataKey
      );
      callback();
    };

    const fetchStockInfo = useCallback(async () => {
      setLoadingStockInfo(true);
      try {
        const data = await stockService.lookup(
          currentStockCode.toUpperCase(),
          currentCategory
        );
        if (isStockLookupRedirectResponse(data)) {
          setEditingStockCode(data.ticker);
          setCurrentStockCode(data.ticker);
        } else if (data.stock) {
          let stockName;
          if (data.stock.name_en) {
            stockName =
              data.stock.category === 'US' // why we have this logic?
                ? uiHelpers.tradChify(data.stock[`name_${i18n.language}`])
                : data.stock[`name_${i18n.language}`];
          } else {
            stockName = data.stock.stockCode;
          }

          setCurrentStock({
            category: data.stock.category,
            ticker: data.stock.stockCode,
            name: stockName,
          });
          setStock(data.stock);
          // if user search a different stock in priceviewer, the priceviewer chart should change its region
          setRegion(categoryToRegin(data.stock.category));

          setLoadingStockInfo(false);
          setError(null);

          // only registered users can make requests to list tips.
          if (availableFeatures.includes('PRICE_VIEWER_EXPERT_TIP')) {
            dispatch(
              fetchStockExpertTips(currentStockCode.toUpperCase(), influencerId)
            );
          }
        }
      } catch (error) {
        generalErrorHandler(dispatch, () => {
          setError(error);
          setLoadingStockInfo(false);
        })(error);
      }
    }, [
      availableFeatures,
      currentStockCode,
      currentCategory,
      dispatch,
      i18n.language,
      influencerId,
    ]);

    useEffect(() => {
      void fetchStockInfo();
    }, [fetchStockInfo]);

    const timeSeriesQueryUnit: TimeSeries.Unit = useMemo(() => {
      if (unit === 'd') {
        return 'DAY';
      }
      if (unit === 'm') {
        return 'MONTH';
      }
      if (unit === 'w') {
        return 'WEEK';
      }
      if (isUnitMinute) {
        switch (minuteUnit) {
          case '5m': {
            return 'FIVE_MINUTE';
          }
          case '3m': {
            return 'THREE_MINUTE';
          }
          case '15m': {
            return 'FIFTEEN_MINUTE';
          }
          case '1h': {
            return 'ONE_HOUR';
          }
          default: {
            return 'MINUTE';
          }
        }
      }
      return 'DAY';
    }, [isUnitMinute, minuteUnit, unit]);

    const enabledMarketBreadth = availableFeatures.includes(
      'PRICE_VIEWER_MARKET_BREADTH'
    );
    const enabledNopStrategy = availableFeatures.includes(
      'PRICE_VIEWER_NOP_STRATEGY'
    );

    const timeSeriesQueryData = useMemo(() => {
      if (!currentStock) {
        return [];
      }
      const data = [getOLHCVQueryString(currentStock)];
      if (
        currentStock.category === 'HK' &&
        eligibleForHKEXProfile(currentStock.ticker)
      ) {
        data.push(getCCASSQueryString(currentStock));
      }
      for (const comparisonStock of comparisonStockList) {
        data.push(getOLHCVQueryString(comparisonStock));
      }
      if (enabledMarketBreadth && stockHasMarketSMA(currentStock.ticker)) {
        data.push(getMarketBreadthQueryString(currentStock));
      }
      if (stockIsUSIndex(currentStock.ticker)) {
        data.push('FEARGREED', 'AAII');
      }
      if (enablePd && currentStock.ticker === '^HSI') {
        data.push('HSIPE', 'HSIDIVIDEND', 'HSIPB', 'HSISHORTRATIO');
      }
      if (enabledMarketBreadth && currentStock.ticker === 'BTCUSDT') {
        data.push('CRYPTOBREADTH');
      }
      if (enabledNopStrategy && currentStock.category === 'HK') {
        data.push(getNOPStrategyString(currentStock));
      }
      return data;
    }, [
      comparisonStockList,
      currentStock,
      enablePd,
      enabledMarketBreadth,
      enabledNopStrategy,
    ]);

    const fetchTimeSeries = useCallback(
      async (lastDate?: string) => {
        if (timeSeriesQueryData.length === 0) {
          return;
        }
        setLoadingTimeSeries(true);

        const timeSeriesData = await dailypriceService.timeSeries(
          undefined,
          timeSeriesQueryData,
          timeSeriesQueryUnit,
          lastDate
        );

        setRawTimeSeries(list => {
          // first time load a data:
          if (!lastDate) {
            return timeSeriesData.data;
          }
          // sometimes we might have duplicate requests,
          // here we make sure the new data is always in order.
          // If out of order, ignore the data:
          if (
            timeSeriesData.data.length > 0 &&
            list.length > 0 &&
            timeSeriesData.data[0].dt >= list[0].dt
          ) {
            return list;
          }
          // combining the data:
          return [...timeSeriesData.data, ...list];
        });
        setLoadingTimeSeries(false);
      },
      [timeSeriesQueryData, timeSeriesQueryUnit]
    );

    useEffect(() => {
      void fetchTimeSeries();
    }, [fetchTimeSeries]);
    const validStrategyList = useMemo(() => {
      const strategyList = [];
      if (
        rawTimeSeries.length > 0 &&
        getDisplayNOPStrategy(rawTimeSeries, currentStock)
      ) {
        let isAlpha = false;
        let isBeta = false;
        for (const rawData of rawTimeSeries) {
          if (rawData[getNOPStrategyString(currentStock)] !== undefined) {
            if (
              'stategy_0_signal' in rawData[getNOPStrategyString(currentStock)]
            ) {
              isAlpha = true;
            }
            if (
              'stategy_1_signal' in rawData[getNOPStrategyString(currentStock)]
            ) {
              isBeta = true;
            }
          }
        }
        if (isAlpha) {
          strategyList.push('alpha');
        }
        if (isBeta) {
          strategyList.push('beta');
        }
      }
      return strategyList;
    }, [rawTimeSeries, currentStock]);
    const loadMoreTimeSeries = useCallback(() => {
      const lastDate =
        rawTimeSeries.length > 0
          ? rawTimeSeries[0]['dt'].slice(0, 8) // only need to keep date part
          : undefined;
      fetchTimeSeries(lastDate);
    }, [fetchTimeSeries, rawTimeSeries]);

    const fetchComparisonStock = async (stockCode: string) => {
      try {
        const data = await stockService.lookup(stockCode.toUpperCase());
        if (isStockLookupRedirectResponse(data)) {
          return await fetchComparisonStock(data.ticker);
        } else if (
          data.stock &&
          data.stock.stockCode !== currentStockCode &&
          comparisonStockList.filter(
            ({ category, ticker }) =>
              data.stock.stockCode === ticker &&
              data.stock.category === category
          ).length === 0
        ) {
          let comparisonStockName;
          if (data.stock.name_en) {
            comparisonStockName =
              data.stock.category === 'US'
                ? uiHelpers.tradChify(data.stock[`name_${i18n.language}`])
                : data.stock[`name_${i18n.language}`];
          } else {
            comparisonStockName = data.stock.stockCode;
          }
          setComparisonStockList(list => [
            ...list,
            {
              category: data.stock.category,
              ticker: data.stock.stockCode,
              name: comparisonStockName,
            },
          ]);
        }
        setError(null);
      } catch (error) {
        generalErrorHandler(dispatch, () => {
          setError(error);
        })(error);
      }
    };

    const { data: favEvents, refetch: fetchFavEvents } = useQuery(
      ['listFavoriteStockEvents', category, stock?.stock_id],
      () =>
        miscService.listFavoriteStockEvents(stock?.category, stock?.stock_id),
      { enabled: false }
    );

    useEffect(() => {
      if (user && stock) {
        fetchFavEvents();
      }
    }, [fetchFavEvents, stock, user]);

    const dataPoints: PriceDataPoint[] = useMemo(() => {
      if (!currentStock) {
        return [];
      }

      const olhcvKey = getOLHCVQueryString(currentStock);
      const ccassKey = getCCASSQueryString(currentStock);

      return rawTimeSeries.map(item => {
        const dt = item['dt'] as string;
        const dtWithDash = awsDTToEChartDT(dt);

        const olhcv = item[olhcvKey] || {};
        const ccass = item[ccassKey] || {};

        const southboundA00003 =
          ccass['southbound_a00003'] == null ? 0 : ccass['southbound_a00003'];
        const southboundA00004 =
          ccass['southbound_a00004'] == null ? 0 : ccass['southbound_a00004'];
        return {
          date: dtWithDash,
          open: olhcv['o'],
          high: olhcv['h'],
          low: olhcv['l'],
          close: olhcv['c'],
          volume: olhcv['v'],
          turnover: olhcv['t'],
          total_no: ccass['nop'],
          shifted_total_no: ccass['shifted_nop'],
          top3concen: ccass['top3'],
          top5concen: ccass['top5'],
          top10concen: ccass['top10'],
          top15concen: ccass['top15'],
          southbound:
            ccass['southbound_a00003'] || ccass['southbound_a00004']
              ? southboundA00003 + southboundA00004
              : undefined,
        };
      });
    }, [currentStock, rawTimeSeries]);

    useEffect(() => {
      const scrollToChart = () => {
        const scrollTargetElement = document.querySelectorAll(
          '.price-viewer-preference'
        )[0];
        if (scrollToChartWhenComplete && scrollTargetElement) {
          const scrollManager = new Scroll();
          scrollManager.scrollTo.element(scrollTargetElement, 0.1, 500);
        }
      };
      if (firstLoad) {
        setFirstLoad(false);
        scrollToChart();
      }
    }, [firstLoad, scrollToChartWhenComplete]);

    const toggleShowParticipantNumber = wrapAnalytics('participant_no', () => {
      dispatch(
        preferenceActions.setPriceViewerParticipantsNumber(
          !showParticipantsNumber
        )
      );
      if (!showParticipantsNumber) {
        dispatch(
          preferenceActions.setPriceViewerShiftedParticipantsNumber(false)
        );
      }
    });

    const { tips } = useSelector(state => state.tips.stockExpertTips);
    const [show, setShow] = useState(false);
    const handleClose = () => {
      setShow(false);
      setComparisonSearchInput('');
      if (handleComparisonModal) {
        handleComparisonModal(false);
      }
    };
    const handleShow = () => setShow(true);

    useEffect(() => {
      setShow(showComparisonModal);
    }, [showComparisonModal]);

    const handleComparisonStockList = (stockCode: string) => {
      fetchComparisonStock(stockCode);
    };

    const handleDeleteComparisonPrice = (index: number) => {
      setComparisonStockList(list => {
        const newComparisonStockList = [...list];
        newComparisonStockList.splice(index, 1);
        return newComparisonStockList;
      });
      handleClose();
    };

    const bidAsk = useBidAsk(
      currentStockCode,
      availableFeatures.includes('PRICE_VIEWER_BID_ASK') &&
        (showRetailTrades || showInstitutionalTrades)
    );

    // this function is a collection of all indicators and exceeding complexity is not an issue:
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const chartGenerators = useMemo(() => {
      const marketbreadthKey =
        currentStock && getMarketBreadthQueryString(currentStock);
      const generators: ChartGenerator[] = [];
      if (!isAdmin) {
        generators.push(
          new Volume(
            !priceViewerVolumeOverlap,
            uiHelpers.candleStickColorMap(graphColorStyle).up,
            uiHelpers.candleStickColorMap(graphColorStyle).down,
            t,
            i18n
          )
        );
      }
      if (!stockIsHKIndex(stockCode) && isAdmin) {
        generators.push(
          new NewVolume(
            !priceViewerVolumeOverlap,
            uiHelpers.candleStickColorMap(graphColorStyle).up,
            uiHelpers.candleStickColorMap(graphColorStyle).down,
            t,
            i18n
          )
        );
      }
      if (!stockIsUSIndex(stockCode) && isAdmin) {
        generators.push(
          new Turnover(
            !priceViewerTurnoverOverlap,
            uiHelpers.candleStickColorMap(graphColorStyle).up,
            uiHelpers.candleStickColorMap(graphColorStyle).down,
            t,
            i18n
          )
        );
      }
      if (showMA) {
        for (const interval of MAIntervals) {
          generators.push(new MA(MAType, interval, t));
        }
      }
      if (showMCAD) {
        generators.push(new MACD(t));
      }
      if (showBollingerBand) {
        generators.push(new BB(t));
      }
      if (showRSI) {
        generators.push(new RSI(RSIInterval, t));
      }
      if (showATR) {
        generators.push(new ATR(ATRInterval, t));
      }
      if (comparisonStockList.length > 0) {
        for (const comparisonStock of comparisonStockList) {
          const olhcvKey = getOLHCVQueryString(comparisonStock);
          generators.push(
            new Comparison(
              comparisonStock.name,
              t,
              rawTimeSeries.map(item => {
                const dt = item['dt'] as string;
                const olhcv = item[olhcvKey] || {};
                return [awsDTToEChartDT(dt), olhcv['c']];
              }),
              comparisonColorStyle
            )
          );
        }
      }
      if (stockIsUSIndex(stockCode) && showFearGreedIndex) {
        generators.push(
          new FearGreedIndex(
            t,
            rawTimeSeries.map(item => {
              const dt = item['dt'] as string;
              const feargreed = item['FEARGREED'] || {};
              return { ...feargreed, date: awsDTToEChartDT(dt) };
            })
          )
        );
      }
      if (stockIsUSIndex(stockCode) && showAAII) {
        for (const direction of ['bullish', 'bearish']) {
          generators.push(
            new AAII(
              t,
              rawTimeSeries.map(item => {
                const dt = item['dt'] as string;

                if (item['AAII']) {
                  const aaii = item['AAII'][direction] || {};
                  return {
                    ...aaii,
                    date: awsDTToEChartDT(dt),
                    updateTime: item['AAII']['update_time'],
                  };
                } else {
                  return { date: awsDTToEChartDT(dt) };
                }
              }),
              direction
            )
          );
        }
      }
      if (availableFeatures.includes('PRICE_VIEWER_MARKET_BREADTH')) {
        if (showMarketSMA && stockHasMarketSMA(currentStockCode)) {
          generators.push(
            new ConstituentSMA(
              rawTimeSeries.map(item => {
                const dt = item['dt'] as string;
                const marketbreadth =
                  (item[marketbreadthKey] &&
                    item[marketbreadthKey]['market']) ||
                  {};
                const updateTime =
                  (item[marketbreadthKey] &&
                    item[marketbreadthKey]['market'] &&
                    item[marketbreadthKey]['market']['update_time']) ||
                  null;
                return {
                  ...marketbreadth,
                  date: awsDTToEChartDT(dt),
                  updateTime: updateTime,
                };
              }),
              'market',
              marketSMAIntervals,
              t
            )
          );
        }
        if (showIndexSMA && stockIsIndex(currentStockCode)) {
          generators.push(
            new ConstituentSMA(
              rawTimeSeries.map(item => {
                const dt = item['dt'] as string;
                const marketbreadth =
                  (item[marketbreadthKey] &&
                    item[marketbreadthKey]['constituent']) ||
                  {};
                const updateTime =
                  (item[marketbreadthKey] &&
                    item[marketbreadthKey]['constituent'] &&
                    item[marketbreadthKey]['constituent']['update_time']) ||
                  null;
                return {
                  ...marketbreadth,
                  date: awsDTToEChartDT(dt),
                  updateTime: updateTime,
                };
              }),
              'constituent',
              indexSMAIntervals,
              t
            )
          );
        }
        if (showMarketSMA && currentStockCode === 'BTCUSDT') {
          generators.push(
            new ConstituentSMA(
              rawTimeSeries.map(item => {
                const dt = item['dt'] as string;
                const marketbreadth = item['CRYPTOBREADTH'] || {};
                return { ...marketbreadth, date: awsDTToEChartDT(dt) };
              }),
              'market',
              marketSMAIntervals,
              t
            )
          );
        }
      }
      if (
        enablePd &&
        showHSIPE &&
        showHSIFundamental &&
        stockIsHKIndex(currentStockCode)
      ) {
        generators.push(
          new HSIPE(
            t,
            rawTimeSeries.map(item => {
              const dt = item['dt'] as string;
              if (item['HSIPE']) {
                const hsipe = item['HSIPE']['pe'] || {};
                return {
                  ...hsipe,
                  pe: hsipe,
                  date: awsDTToEChartDT(dt),
                  updateTime: item['HSIPE']['update_time'],
                };
              } else {
                return { date: awsDTToEChartDT(dt) };
              }
            })
          )
        );
      }
      if (
        enablePd &&
        showHSIPB &&
        showHSIFundamental &&
        stockIsHKIndex(currentStockCode)
      ) {
        generators.push(
          new HSIPB(
            t,
            rawTimeSeries.map(item => {
              const dt = item['dt'] as string;
              if (item['HSIPB']) {
                const hsipb = item['HSIPB']['pb'] || {};
                return {
                  ...hsipb,
                  pb: hsipb,
                  date: awsDTToEChartDT(dt),
                  updateTime: item['HSIPB']['update_time'],
                };
              } else {
                return { date: awsDTToEChartDT(dt) };
              }
            })
          )
        );
      }
      if (
        enablePd &&
        showHSIDividend &&
        showHSIFundamental &&
        stockIsHKIndex(currentStockCode)
      ) {
        generators.push(
          new HSIDividend(
            t,
            rawTimeSeries.map(item => {
              const dt = item['dt'] as string;
              if (item['HSIDIVIDEND']) {
                const hsiDividend = item['HSIDIVIDEND']['dividend'] || {};
                return {
                  ...hsiDividend,
                  dividend: item['HSIDIVIDEND']['dividend'],
                  date: awsDTToEChartDT(dt),
                  updateTime: item['HSIDIVIDEND']['update_time'],
                };
              } else {
                return { date: awsDTToEChartDT(dt) };
              }
            })
          )
        );
      }
      if (
        enablePd &&
        showHSIShortRatio &&
        showHSIFundamental &&
        stockIsHKIndex(currentStockCode)
      ) {
        generators.push(
          new HSIShortRatio(
            t,
            rawTimeSeries.map(item => {
              const dt = item['dt'] as string;
              if (item['HSISHORTRATIO']) {
                const hsiShortaRtio = item['HSISHORTRATIO']['ratio'] || {};
                return {
                  ...hsiShortaRtio,
                  ratio: item['HSISHORTRATIO']['ratio'],
                  date: awsDTToEChartDT(dt),
                  updateTime: item['HSISHORTRATIO']['update_time'],
                };
              } else {
                return { date: awsDTToEChartDT(dt) };
              }
            })
          )
        );
      }
      if (region === 'hk' && eligibleForHKEXProfile(stockCode)) {
        // CCASS related indicators
        if (showParticipantsNumber) {
          generators.push(new NOP(false, t, nopColorStyleV2));
        }
        if (showShiftedParticipantsNumber) {
          generators.push(new NOP(true, t, nopColorStyleV2));
        }
        if (showTopN) {
          generators.push(
            new Concentration(priceViewerTopN, t, concentrationColorStyleV2)
          );
        }
        if (showSouthbound && isSouthboundStock) {
          generators.push(new Southbound(t));
        }
        if (
          showNOPStrategy &&
          getDisplayNOPStrategy(rawTimeSeries, currentStock)
        ) {
          const nopStrategyData = [];
          const shiftedTimeSeries = rawTimeSeries;
          const nopStrategyString = getNOPStrategyString(currentStock);
          if (!(getOLHCVQueryString(currentStock) in rawTimeSeries.at(-1))) {
            const newSignal = [
              rawTimeSeries.at(-1)[nopStrategyString]['stategy_0_signal'],
              rawTimeSeries.at(-1)[nopStrategyString]['stategy_1_signal'],
            ];
            shiftedTimeSeries.pop();
            shiftedTimeSeries[rawTimeSeries.length - 1][nopStrategyString][
              'stategy_0_signal'
            ] = newSignal[0];
            shiftedTimeSeries[rawTimeSeries.length - 1][nopStrategyString][
              'stategy_1_signal'
            ] = newSignal[1];
          }
          for (const item of shiftedTimeSeries) {
            const dt = item['dt'] as string;
            const nopStrategy = item[nopStrategyString] || {};
            const o = (item[getOLHCVQueryString(currentStock)] || {})['o'];
            if (nopStrategy['stategy_0_signal']) {
              nopStrategyData.push({
                capital_sh: nopStrategy['capital_sh'],
                stategy_0_capital: nopStrategy['stategy_0_capital'],
                stategy_1_capital: nopStrategy['stategy_1_capital'],
                stategy_0_signal: nopStrategy['stategy_0_signal'],
                stategy_1_signal: nopStrategy['stategy_1_signal'],
                date: awsDTToEChartDT(dt),
                open: o,
              });
            }
          }
          const checkIsValidStrategy = obj =>
            obj[getActiveStrategyByName(NOPStrategyVersion) + '_signal'];
          if (nopStrategyData.some(checkIsValidStrategy)) {
            generators.push(
              new NOPStrategy(
                nopStrategyData,
                NOPStrategyVersion,
                t,
                strategyMode
              ),
              new NOPStrategySignalArea(nopStrategyData, NOPStrategyVersion, t)
            );
          }
        }
      }
      if (unit === 'd') {
        // daily only indicators
        if (priceViewerLabelType === TIPS_TAB_KEY) {
          generators.push(new Tips(tips));
        } else if (showNOPStrategy && currentStock) {
          if (
            rawTimeSeries.length > 0 &&
            getDisplayNOPStrategy(rawTimeSeries, currentStock)
          ) {
            generators.push(
              new NOPStrategySignal(
                rawTimeSeries.map(item => {
                  const dt = item['dt'] as string;
                  const nopStrategy =
                    item[getNOPStrategyString(currentStock)] || {};
                  const strategy = getActiveStrategyByName(NOPStrategyVersion);
                  return {
                    signal: nopStrategy[strategy + '_signal'],
                    date: awsDTToEChartDT(dt),
                  };
                })
              )
            );
          }
        } else {
          generators.push(new Favorites(favEvents?.events || []));
        }
        if (probabilityData) {
          generators.push(new Probability(t, probabilityData));
        }
        if (signalOccurrences) {
          generators.push(new SignalOccurence(signalOccurrences));
        }
        if (
          region === 'hk' &&
          eligibleForHKEXProfile(stockCode) &&
          availableFeatures.includes('PRICE_VIEWER_BID_ASK')
        ) {
          if (showRetailTrades) {
            generators.push(
              new RetailInstitutionalTrades(
                bidAsk,
                RetailInstitutionalTradesEnum.retailTrades,
                t,
                uiHelpers.candleStickColorMap(graphColorStyle).down,
                uiHelpers.candleStickColorMap(graphColorStyle).up,
                retailTradesVersion
              )
            );
          }
          if (showInstitutionalTrades) {
            generators.push(
              new RetailInstitutionalTrades(
                bidAsk,
                RetailInstitutionalTradesEnum.institutionalTrades,
                t,
                uiHelpers.candleStickColorMap(graphColorStyle).down,
                uiHelpers.candleStickColorMap(graphColorStyle).up,
                institutionalTradesVersion
              )
            );
          }
        }
      }
      return generators;
    }, [
      availableFeatures,
      enablePd,
      priceViewerVolumeOverlap,
      priceViewerTurnoverOverlap,
      graphColorStyle,
      t,
      i18n,
      showMA,
      showRSI,
      showMCAD,
      showBollingerBand,
      unit,
      MAIntervals,
      MAType,
      RSIInterval,
      priceViewerLabelType,
      probabilityData,
      signalOccurrences,
      region,
      isAdmin,
      stockCode,
      showATR,
      showMarketSMA,
      currentStockCode,
      showIndexSMA,
      tips,
      favEvents?.events,
      showParticipantsNumber,
      showShiftedParticipantsNumber,
      showTopN,
      priceViewerTopN,
      showRetailTrades,
      showInstitutionalTrades,
      ATRInterval,
      marketSMAIntervals,
      indexSMAIntervals,
      retailTradesVersion,
      institutionalTradesVersion,
      comparisonStockList,
      nopColorStyleV2,
      showHSIPB,
      showHSIFundamental,
      showHSIShortRatio,
      concentrationColorStyleV2,
      comparisonColorStyle,
      bidAsk,
      rawTimeSeries,
      showFearGreedIndex,
      showAAII,
      showSouthbound,
      isSouthboundStock,
      currentStock,
      NOPStrategyVersion,
      showNOPStrategy,
      strategyMode,
      showHSIDividend,
      showHSIPE,
    ]);
    const [comparisonSearchInput, setComparisonSearchInput] = useState('');
    const handleComparisonSearchInput = (input: string) => {
      setComparisonSearchInput(input);
    };
    const hasMembership = useMembership();
    return (
      <div className="light-bg">
        <ComparisonSearch
          show={show}
          handleClose={handleClose}
          handleComparisonStockList={handleComparisonStockList}
          comparisonStockNames={comparisonStockList.map(({ name }) => name)}
          handelDeleteComparisonPrice={handleDeleteComparisonPrice}
          t={t}
          stockInput={comparisonSearchInput}
          handleStockInput={handleComparisonSearchInput}
        />
        <CSSTransition
          in={showPriceViewerPreferences}
          timeout={300}
          classNames="animated-height-price-viewer-preferences"
          unmountOnExit
        >
          <PriceViewerPreferences
            wrapAnalytics={wrapAnalytics}
            stock={stock}
            region={region}
            editingStockCode={editingStockCode}
            handleEditingStockCode={handleEditingStockCode}
            handleSearchInputKeyPressed={handleSearchInputKeyPressed}
            handleSearchClick={handleSearchClick}
            stockCode={stockCode}
            toggleShowParticipantNumber={toggleShowParticipantNumber}
            noLittleSearchField={noLittleSearchField}
            isSouthboundStock={isSouthboundStock}
            showStrategy={getDisplayNOPStrategy(rawTimeSeries, currentStock)}
            validStrategyList={validStrategyList}
          />
        </CSSTransition>{' '}
        {!window.location.pathname.includes('/lookup') && (
          <span
            style={{
              position: 'relative',
              padding: '0 5%',
            }}
          >
            <Button
              variant="primary"
              onClick={handleShow}
              style={{
                display: availableFeatures.includes('PRICE_VIEWER_COMPARE')
                  ? ''
                  : 'none',
                backgroundColor: 'white',
                color: '#000',
                border: '2px solid #000',
                borderRadius: '5px',
                fontSize: '14px',
                padding: '2px 8px',
              }}
            >
              <FaPlus style={{ marginRight: '2px', color: 'green' }} />
              {t('comparisonStock')}
            </Button>
          </span>
        )}
        <div
          style={{
            marginTop: 15,
            position: 'relative', // make childs in priceviewer chart absolute to correct box
          }}
        >
          {error ? (
            t('something-wrong')
          ) : (
            <>
              {dataPoints.length === 0 &&
                !(loadingStockInfo || loadingTimeSeries) && (
                  <span style={{ textAlign: 'center' }}>
                    <Description>{t('noDataFetched')}</Description>
                  </span>
                )}
              <PriceViewerChart
                loading={loadingStockInfo || loadingTimeSeries}
                onLoadMore={loadMoreTimeSeries}
                priceData={dataPoints}
                chartGenerators={chartGenerators}
                selectedPeriodPreset={period}
                bullishColor={uiHelpers.candleStickColorMap(graphColorStyle).up}
                bearishColor={
                  uiHelpers.candleStickColorMap(graphColorStyle).down
                }
                useIXICTradingViewPrice={
                  stockCode === '^IXIC' && !hasMembership
                }
              />
              {region === 'hk' && eligibleForHKEXProfile(stockCode) && (
                <CCASSDescription />
              )}
              {region === 'hk' &&
                showRetailTrades &&
                eligibleForHKEXProfile(stockCode) && (
                  <BlurPlaceholder
                    featureKey={`retail-trade-${retailTradesVersion}`}
                  />
                )}
              {region === 'hk' &&
                showInstitutionalTrades &&
                eligibleForHKEXProfile(stockCode) && (
                  <BlurPlaceholder
                    featureKey={`institutional-trades-${institutionalTradesVersion}`}
                  />
                )}

              {isCryptoStock && (
                <Description>
                  <span style={{ color: '#6c757d' }}>
                    {t('cryptoDescription')}
                  </span>
                </Description>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
);

export default PriceViewer;
